import type { CSSProperties } from "react";
import { CORE_COLORS, CORE_FONT } from "@/lib/theme/core";

export const AUTH_LABEL_STYLE: CSSProperties = {
  fontFamily: CORE_FONT.sans,
  fontSize: "13px",
  fontWeight: 700,
  letterSpacing: "0.2px",
  lineHeight: "18px",
  color: CORE_COLORS.textPrimary,
};

export const AUTH_INPUT_STYLE: CSSProperties = {
  fontFamily: CORE_FONT.sans,
  fontSize: "15px",
  color: CORE_COLORS.textPrimary,
  lineHeight: "22px",
  padding: "12px 14px",
  height: "48px",
  border: `1px solid rgba(0,0,0,0.3)`,
  borderRadius: "3px",
  background: "#ffffff",
  outline: "none",
  width: "100%",
};

export const AUTH_PLACEHOLDER_COLOR = CORE_COLORS.textTertiary;

/**
 * CJK line-breaking.
 *
 * QA reported wrong break points on all three viewports across all four auth
 * pages ("文字折斷不正確" / "文字截断不正确"). Browsers break Chinese between
 * any two characters by default, which splits words and, for a 28px condensed
 * heading in a narrow column, reads as broken.
 *
 * `wordBreak: keep-all` stops mid-word breaks in CJK; `overflowWrap: anywhere`
 * is the escape valve so an unbreakable run (a long email, a URL) still wraps
 * instead of overflowing. `lineBreak: strict` applies the stricter CJK rules
 * for trailing punctuation.
 */
const CJK_WRAP: CSSProperties = {
  wordBreak: "keep-all",
  overflowWrap: "anywhere",
  lineBreak: "strict",
};

export const AUTH_HEADING_STYLE: CSSProperties = {
  fontFamily: CORE_FONT.condensed,
  fontSize: "28px",
  fontWeight: 700,
  letterSpacing: "-0.4px",
  lineHeight: 1.1,
  color: CORE_COLORS.textPrimary,
  textAlign: "center",
  ...CJK_WRAP,
};

export const AUTH_SUBHEADING_STYLE: CSSProperties = {
  fontFamily: CORE_FONT.sans,
  fontSize: "15px",
  color: CORE_COLORS.textSecondary,
  lineHeight: 1.55,
  textAlign: "center",
  ...CJK_WRAP,
};

/**
 * Hover on the auth controls.
 *
 * Every one of these used to carry its resting `backgroundColor` / `color` as
 * an inline declaration, and each call site wrote a `hover:bg-*` utility
 * beside it. An inline declaration outranks every class, so not one of those
 * hovers had ever applied — the buttons and links on all six auth forms were
 * hover-dead. QA: "ensure links and buttons hover effect".
 *
 * The resting value now travels as a custom property and is applied by a
 * class, so both states are classes and the `hover:` variant wins normally.
 * Pair each `*_STYLE` with its `*_CLASS`; neither renders correctly alone.
 */
export const AUTH_PRIMARY_BUTTON_STYLE: CSSProperties = {
  fontFamily: CORE_FONT.sans,
  fontSize: "15px",
  fontWeight: 700,
  color: "#ffffff",
  lineHeight: "20px",
  padding: "14px 20px",
  borderRadius: "3px",
  width: "100%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  ["--rest-bg" as string]: CORE_COLORS.brandGreen,
  ["--hover-bg" as string]: CORE_COLORS.brandGreenDark,
} as CSSProperties;

export const AUTH_PRIMARY_BUTTON_CLASS =
  "bg-[color:var(--rest-bg)] transition-colors duration-150 hover:bg-[color:var(--hover-bg)] active:scale-[0.99] motion-reduce:transition-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[color:var(--rest-bg)]";

export const AUTH_SECONDARY_LINK_STYLE: CSSProperties = {
  fontFamily: CORE_FONT.sans,
  fontSize: "14px",
  fontWeight: 700,
  lineHeight: "20px",
  textDecoration: "underline",
  textUnderlineOffset: "3px",
  ["--rest-fg" as string]: CORE_COLORS.brandGreen,
  ["--hover-fg" as string]: CORE_COLORS.brandGreenDark,
} as CSSProperties;

export const AUTH_SECONDARY_LINK_CLASS =
  "text-[color:var(--rest-fg)] transition-colors duration-150 hover:text-[color:var(--hover-fg)] motion-reduce:transition-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[color:var(--rest-fg)]";

export const AUTH_SECONDARY_BUTTON_STYLE: CSSProperties = {
  fontFamily: CORE_FONT.sans,
  fontSize: "15px",
  fontWeight: 700,
  color: CORE_COLORS.textPrimary,
  lineHeight: "20px",
  padding: "14px 20px",
  border: `1px solid rgba(0,0,0,0.3)`,
  borderRadius: "3px",
  width: "100%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  ["--rest-bg" as string]: "#ffffff",
  ["--hover-bg" as string]: "#f2f2f4",
} as CSSProperties;

export const AUTH_SECONDARY_BUTTON_CLASS =
  "bg-[color:var(--rest-bg)] transition-colors duration-150 hover:bg-[color:var(--hover-bg)] active:scale-[0.99] motion-reduce:transition-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[color:var(--rest-bg)]";
